import {
  AgentDirectNewtsControlMessage,
  AgentDirectNewtsMessage
} from '@tryiris-ai/common.protojs/api/v1/agent_direct';
import { v4 as uuidv4 } from 'uuid';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { toHexFromId } from '#~/api/utils.js';

interface Msg {
  data: Uint8Array;
  reject?: boolean;
  reply?: string;
}

type MsgHandler = (msg: Msg) => void;

interface Subscription {
  id: string;
  subject: string;
  handler: MsgHandler;
}

class Router {
  private internalSubscriptions: { [key: string]: Subscription[] } = {};
  private externalSubscriptions: { [key: string]: string[] } = {};

  public onPublishMap: Record<
    string,
    (
      msg: Uint8Array,
      replyTo: T_Maybe<string>,
      options?: { mode?: 'local' | 'cloud' | 'hybrid'; lossy?: boolean; subject: string }
    ) => Promise<void> | void
  > = {};

  private messageBuffer = new Map<
    string,
    {
      [key: number]: Uint8Array;
    }
  >();

  private recentMessageIds = new Set<string>();

  public async parseIncomingMessage(participant: string, msg: Uint8Array): Promise<void> {
    try {
      const m = AgentDirectNewtsMessage.decode(msg);

      const msgIdStr = toHexFromId(m.id)!;

      if (m.segmentCount > 1) {
        if (!this.messageBuffer.has(msgIdStr)) {
          this.messageBuffer.set(msgIdStr, {});
        }

        const buffer = this.messageBuffer.get(msgIdStr)!;

        buffer[m.segmentIndex] = m.payload!;

        if (Object.keys(buffer).length !== m.segmentCount) {
          return;
        }

        const segments = Object.values(buffer);
        // each segment is 32kb max
        const fullMsg = new Uint8Array(m.segmentCount * 32 * 1024);

        let offset = 0;
        for (const segment of segments) {
          fullMsg.set(segment, offset);
          offset += segment.length;
        }

        this.messageBuffer.delete(msgIdStr);

        m.payload = fullMsg;
      }

      if (this.recentMessageIds.has(msgIdStr)) {
        return;
      }

      this.recentMessageIds.add(msgIdStr);

      if (this.recentMessageIds.size > 100) {
        // Remove the last item from the set.
        const iter = this.recentMessageIds.values();

        const last = iter.next().value;

        if (last !== undefined) {
          this.recentMessageIds.delete(last);
        }
      }

      if (m.control) {
        this.handleControlMessage(participant, m.control);
      }

      if (m.subject) {
        this.handleIncomingMessage(m);
      }
    } catch (e) {}
  }

  private handleControlMessage(participant: string, control: AgentDirectNewtsControlMessage): void {
    if (!this.externalSubscriptions[participant]) {
      this.externalSubscriptions[participant] = [];
    }

    if (control.reset) {
      this.externalSubscriptions[participant] = [];
    }

    this.externalSubscriptions[participant]!.push(...control.subscribe);

    for (const unsubscribe of control.unsubscribe) {
      const subs = this.externalSubscriptions[participant];

      if (!subs) {
        continue;
      }

      const index = subs.indexOf(unsubscribe);
      if (index !== -1) {
        subs.splice(index, 1);
      }
    }
  }

  private handleIncomingMessage(msg: AgentDirectNewtsMessage): void {
    const subs = this.internalSubscriptions[msg.subject] ?? [];

    for (const sub of subs) {
      sub.handler({ data: msg.payload!, reject: !!msg.control?.reject, reply: msg.replyTo });
    }
  }

  public subscribe(agentId: string, subject: string, f: MsgHandler): Subscription {
    const subscription: Subscription = {
      subject,
      id: uuidv4(),
      handler: f
    };

    if (!this.internalSubscriptions[subject]) {
      this.internalSubscriptions[subject] = [];
    }

    this.internalSubscriptions[subject]!.push(subscription);

    const controlMsg = AgentDirectNewtsMessage.fromPartial({
      control: {
        subscribe: [subject]
      }
    });

    this.publish(agentId, '$CTRL', new Uint8Array(), controlMsg);

    return subscription;
  }

  public unsubscribe(agentId: string, subscription: Subscription): void {
    const subs = this.internalSubscriptions[subscription.subject];

    if (!subs) {
      return;
    }

    const index = subs.findIndex((sub) => sub.id === subscription.id);
    if (index !== -1) {
      subs.splice(index, 1);
    }

    if (subs.length === 0) {
      delete this.internalSubscriptions[subscription.subject];
    }

    const controlMsg = AgentDirectNewtsMessage.fromPartial({
      control: {
        unsubscribe: [subscription.subject]
      }
    });

    this.publish(agentId, '$CTRL', new Uint8Array(), controlMsg);
  }

  public async publish(
    agentId: string,
    subject: string,
    data: Uint8Array,
    meta?: AgentDirectNewtsMessage,
    options?: { mode?: 'local' | 'cloud' | 'hybrid'; lossy?: boolean }
  ): Promise<void> {
    const segmentSize = 32 * 1024;
    const segmentCount = Math.ceil(data.length / segmentSize);

    const safeSegmentCount = Math.max(segmentCount, 1);

    const id = uuidv4();

    const promises = [];

    for (let i = 0; i < safeSegmentCount; i++) {
      const msg = meta || AgentDirectNewtsMessage.fromPartial({});

      msg.id = new Uint8Array(id.split('-').map((s) => parseInt(s, 16)));
      msg.subject = subject;

      msg.segmentCount = safeSegmentCount;
      msg.segmentIndex = i;

      msg.payload = data.slice(i * segmentSize, (i + 1) * segmentSize);

      const encodedMsg = AgentDirectNewtsMessage.encode(msg).finish();

      if (this.onPublishMap[agentId]) {
        promises.push(
          this.onPublishMap[agentId]?.(encodedMsg, msg.replyTo, { ...options, subject })
        );
      }
    }

    await Promise.all(promises);
  }

  public async request(
    agentId: string,
    subject: string,
    data: Uint8Array,
    timeout: number,
    reply: T_Maybe<string>
  ): Promise<Msg> {
    const inboxSubj = reply ?? `$INBOX.${uuidv4()}`;

    let resolve: (value: Msg | PromiseLike<Msg>) => void;
    let reject: (reason?: any) => void;

    const promise = new Promise<Msg>((res, rej) => {
      resolve = res;
      reject = rej;
    });

    const sub = this.subscribe(agentId, inboxSubj, (msg) => {
      if (msg.reject) {
        reject();
      }

      resolve(msg);
    });

    const msg = AgentDirectNewtsMessage.fromPartial({
      subject,
      control: {
        subscribe: [inboxSubj]
      },
      replyTo: inboxSubj
    });

    try {
      // Publish inside the try so a publish failure still runs the finally and
      // releases this request's $INBOX subscription. If the await sat outside,
      // a throw skipped the cleanup and leaked one handler per failed request
      // into the module-level subscription map; reset() re-advertises every key,
      // so the $CTRL payload then grows without bound.
      // ENG-4665 H2: arm the reply timeout at the START. Awaiting publish
      // first left mutate hung forever on a wedged LiveKit publisher PC
      // (timer never started). Publish failure still hits finally and
      // releases the $INBOX subscription.
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<never>((_, rejectTimeout) => {
        timer = setTimeout(() => {
          rejectTimeout(new Error('timeout'));
        }, timeout);
      });

      try {
        return await Promise.race([
          this.publish(agentId, subject, data, msg).then(() => promise),
          timeoutPromise
        ]);
      } finally {
        if (timer !== undefined) clearTimeout(timer);
      }
    } finally {
      this.unsubscribe(agentId, sub);
    }
  }

  public reset(agentId: string): void {
    const controlMsg = AgentDirectNewtsMessage.fromPartial({
      control: {
        subscribe: Object.keys(this.internalSubscriptions),

        reset: true
      }
    });

    this.publish(agentId, '$CTRL', new Uint8Array(), controlMsg);
  }
}

export { Router };
export type { MsgHandler, Msg, Subscription };
